import React, {useContext, useEffect, useRef, useState} from 'react';
import PageHeading from "../../../../../../Components/PageHeading";
import {Employee} from "../../../../../../types/demo";
import {useFetch} from "../../../../../../layout/hooks/useFetch";
import callToast, {handleChangeData, mergeWithSelected, validateFormFields} from "../../../../../../utils/helper";
import Label from "../../../../../../Components/Label";
import {InputText} from "primereact/inputtext";
import {Dropdown} from "primereact/dropdown";
import {Button} from "primereact/button";
import {webInfo} from "../../../../../../utils/webInfo";
import {LayoutContext} from "../../../../../../layout/context/LayoutContext";
import {Toast} from "primereact/toast";
import FileUploader from "../../Components/FileUploader";


const AddEmployee = () => {
    const initialState: Employee & { password_confirmation?: string } = {
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        role_id:  null,
        employee_department_id:  null,
        employee_designation_id: null,
        documents: null
    }


    const [formData, setFormData] = useState<any>(initialState)

    const [roleFilter, setRoleFilter] = useState<string>('');
    const [roleOptions, setRoleOptions] = useState<{id: number, name: string}[]>([]);

    const [departmentFilter, setDepartmentFilter] = useState<string>('');
    const [departmentOptions, setDepartmentOptions] = useState<{id: number, name: string}[]>([]);

    const [designationFilter, setDesignationFilter] = useState<string>('');
    const [designationOptions, setDesignationOptions] = useState<{id: number, name: string}[]>([]);

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const toast = useRef<Toast | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const {accessToken} = useContext(LayoutContext)

    const submitFormEndpointUrl = '/employees'


    // fetch dorpdown data
    // role options
    const { data: roleRawData } = useFetch(
        `/roles?page=1&per_page=10${roleFilter ? `&name=${roleFilter}` : ''}`,
        [roleFilter]
    );

    useEffect(() => {
        if (roleRawData) {
            const modifiedData = roleRawData?.map((item: any) => ({
                id: item?.id,
                name: item?.name
            }));
            setRoleOptions(mergeWithSelected(modifiedData, formData?.role_id));
        }
    }, [roleRawData, formData?.role_id]);

    //Department options
    const { data: departmentRawData } = useFetch(
        `/employee-departments?page=1&per_page=10${departmentFilter ? `&name=${departmentFilter}` : ''}`,
        [departmentFilter]
    );

    useEffect(() => {
        if (departmentRawData) {
            const modifiedData = departmentRawData?.map((item: any) => ({
                id: item?.id,
                name: item?.name
            }));
            setDepartmentOptions(mergeWithSelected(modifiedData, formData?.employee_department_id));
        }
    }, [departmentRawData, formData?.employee_department_id]);

    // Designation options
    const { data: designationRawData } = useFetch(
        `/employee-designations?page=1&per_page=10${designationFilter ? `&name=${designationFilter}` : ''}`,
        [designationFilter]
    );

    useEffect(() => {
        if (designationRawData) {
            const modifiedData = designationRawData?.map((item: any) => ({
                id: item?.id,
                name: item?.name,
                document_requirements: item?.document_requirements?.map((docReq: any) => ({
                    id: docReq.id,
                    is_required: docReq.is_required,
                    employee_document_type: docReq.employee_document_type
                }))

            }));
            setDesignationOptions(mergeWithSelected(modifiedData, formData?.employee_designation_id));
        }
    }, [designationRawData, formData?.employee_designation_id]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // 1. Validation Rules
        const validationRules = [
            { field: 'name', label: 'Name' },
            { field: 'email', label: 'Email' },
            { field: 'password', label: 'Password' },
            { field: 'password_confirmation', label: 'Confirm Password' },
            { field: 'role_id', label: 'Role' },
            { field: 'employee_department_id', label: 'Department' },
            { field: 'employee_designation_id', label: 'Designation' },
        ];

        if (!validateFormFields(formData, validationRules, toast)) return;

        // Frontend validation for matching passwords
        if (formData.password !== formData.password_confirmation) {
            callToast(toast, false, "Passwords do not match");
            return;
        }

        // 2. Create FormData object for Multipart submission
        const data = new FormData();

        data.append('name', formData.name);
        data.append('email', formData.email);
        data.append('password', formData.password);
        data.append('role_id', String(formData.role_id?.id || ''));
        data.append('employee_department_id', String(formData.employee_department_id?.id || ''));
        data.append('employee_designation_id', String(formData.employee_designation_id?.id || ''));


        if (formData.documents && formData.documents.length > 0) {
            formData.documents.forEach((doc: any, index: number) => {
                if (doc.document_type_id?.id) {
                    data.append(`documents[${index}][document_type_id]`, String(doc.document_type_id.id));
                }
                if (doc.document instanceof File) {
                    data.append(`documents[${index}][document]`, doc.document);
                } else {
                    data.append(`documents[${index}][document]`, "");
                }
            });
        }



        try {
            setIsSubmitting(true);
            const response = await fetch(`${webInfo?.baseUrl}${submitFormEndpointUrl}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: data,
            });

            if (response.ok) {
                callToast(toast, true, `Employee created successfully`);
                setFormData(initialState);
                setRoleFilter('')
                setDepartmentFilter('')
                setDesignationFilter('')
                return;
            } else {
                const resData = await response.json();
                callToast(toast, false, resData.message || resData.error || "Submission failed");
            }

        } catch (err) {
            console.error('Failed to submit', err);
            callToast(toast, false, "A network error occurred");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={"card"}>
            <Toast ref={toast} />
            <PageHeading title={"Add Employee"} back={true} />

            <form onSubmit={handleSubmit}>
                <div className="grid">
                    <div className="col-12 md:col-3">
                        <Label label={"Name"} required/>
                        <InputText
                            id="name"
                            value={formData.name}
                            onChange={(e) => handleChangeData('name', e.target.value, formData, setFormData)}
                            placeholder={"Enter name"}
                            className={`w-full`}
                        />
                    </div>
                </div>

                {/* Password Row */}
                <div className="grid mt-2">
                    <div className="col-12 md:col-3">
                        <Label label={"Email"} required />
                        <InputText
                            id="email"
                            value={formData.email}
                            onChange={(e) => handleChangeData('email', e.target.value, formData, setFormData)}
                            placeholder={"Enter email"}
                            className={`w-full`}
                            type="email"
                        />
                    </div>
                    <div className="col-12 md:col-2">
                        <Label label={"Password"} required />
                        <InputText
                            id="password"
                            value={formData.password}
                            onChange={(e) => handleChangeData('password', e.target.value, formData, setFormData)}
                            placeholder={"Enter password"}
                            className={`w-full`}
                            type="password"
                        />
                    </div>

                    <div className="col-12 md:col-2">
                        <Label label={"Confirm Password"} required />
                        <InputText
                            id="password_confirmation"
                            value={formData.password_confirmation}
                            onChange={(e) => handleChangeData('password_confirmation', e.target.value, formData, setFormData)}
                            placeholder={"Confirm password"}
                            className={`w-full`}
                            type="password"
                        />
                    </div>
                </div>

                <div className="grid mt-2">
                    <div className="col-12 md:col-2">
                        <Label label={"Employee role"} required/>
                        <Dropdown
                            options={roleOptions? roleOptions : []}
                            optionLabel="name"
                            id="role_id"
                            name={`role_id`}
                            className={`w-full`}
                            value={formData?.role_id}
                            onChange={(e) => handleChangeData('role_id', e.value, formData, setFormData)}
                            placeholder="Select role"
                            filter
                            onFilter={(e) => {
                                if (timeoutRef?.current) {
                                    clearTimeout(timeoutRef.current);
                                }
                                timeoutRef.current = setTimeout(() => {
                                    setRoleFilter(e.filter)
                                }, 500);

                                if(roleRawData){
                                    const modifiedData = roleRawData?.map((item: any) => ({
                                        id: item?.id,
                                        name: item?.name
                                    }));
                                    setRoleOptions(modifiedData);
                                }
                            }}
                            showClear
                            showFilterClear
                        />
                    </div>
                    <div className="col-12 md:col-2">
                        <Label label={"Department"} required/>
                        <Dropdown
                            options={departmentOptions? departmentOptions : []}
                            id="employee_department_id"
                            optionLabel="name"
                            name={`employee_department_id`}
                            className={`w-full`}
                            value={formData?.employee_department_id}
                            onChange={(e) => handleChangeData('employee_department_id', e.value, formData, setFormData)}
                            placeholder="Select department"
                            filter
                            onFilter={(e) => {
                                if (timeoutRef?.current) {
                                    clearTimeout(timeoutRef.current);
                                }
                                timeoutRef.current = setTimeout(() => {
                                    setDepartmentFilter(e.filter)
                                }, 500);

                                if(departmentRawData){
                                    const modifiedData = departmentRawData?.map((item: any) => ({
                                        id: item?.id,
                                        name: item?.name
                                    }));
                                    setDepartmentOptions(modifiedData);
                                }
                            }}
                            showClear
                            showFilterClear
                        />
                    </div>
                    <div className="col-12 md:col-2">
                        <Label label={"Designation"} required/>
                        <Dropdown
                            options={designationOptions? designationOptions : []}
                            id="employee_designation_id"
                            optionLabel="name"
                            name={`employee_designation_id`}
                            className={`w-full`}
                            value={formData?.employee_designation_id}
                            onChange={(e) => {
                                const designation = e.value;
                                const documentRequirements = designation?.document_requirements?.map((req: any) => ({
                                    id: req.id,
                                    document_type_id: req.employee_document_type,
                                    is_required: req.is_required,
                                    document: null
                                })) || null;

                                handleChangeData(
                                    'employee_designation_id',
                                    designation,
                                    formData,
                                    setFormData
                                );
                                setFormData((prev: any) => ({
                                    ...prev,
                                    documents: documentRequirements
                                }));
                            }}

                            placeholder="Select designation"
                            filter
                            onFilter={(e) => {
                                if (timeoutRef?.current) {
                                    clearTimeout(timeoutRef.current);
                                }
                                timeoutRef.current = setTimeout(() => {
                                    setDesignationFilter(e.filter)
                                }, 500);

                                if(designationRawData){
                                    const modifiedData = designationRawData?.map((item: any) => ({
                                        id: item?.id,
                                        name: item?.name,
                                        document_requirements: item?.document_requirements?.map((docReq: any) => ({
                                            id: docReq.id,
                                            is_required: docReq.is_required,
                                            employee_document_type: docReq.employee_document_type
                                        }))
                                    }))
                                    setDesignationOptions(modifiedData);
                                }
                            }}
                            showClear
                            showFilterClear
                        />
                    </div>
                </div>

                <div className="gird md:mt-2">
                    {formData.documents && formData.documents.map((individualDocument: any, idx: number) => {
                        return (
                            <div className="grid align-items-center gap-3" key={idx}>
                                <div className="col-12 md:col-9 md:mt-2">
                                    <Label label={individualDocument?.document_type_id?.name || ''} required={individualDocument?.is_required === 1} />
                                    <FileUploader formData={formData} setFormData={setFormData} index={idx}/>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="flex gap-2 mt-2">
                    <Button
                        type="submit"
                        label={"Create"}
                        className="p-button-primary"
                        size={'small'}
                        loading={isSubmitting}
                    />
                </div>
            </form>
        </div>
    );
};

export default AddEmployee;